import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { fileReviewAPI, clientAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import InfoIconButton from '../components/InfoIconButton';
import './FileReviewCreate.css';

const REVIEW_TYPES = ['Initial', 'Annual1', 'Annual2'];

const PAGE_INFO_TEXT =
  'Create a file review for a client. The active template questions will be copied into the review. You can then send it to the service coordinator (SC) from the review detail page.';

function FileReviewCreate() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    review_type: 'Initial',
    reviewee_staff_id: '',
    reviewer_staff_id: '',
    review_date: '',
    sc_due_date: '',
  });

  useEffect(() => {
    if (!hasPermission('file_reviews.create')) {
      navigate(tenantPath('/lists/file-reviews'), { replace: true });
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const [cRes, sRes] = await Promise.all([
          clientAPI.getAll({ page: 1, pageSize: 1000 }),
          staffAPI.getAll({ active: 'true' }),
        ]);
        setClients(cRes.data?.data ?? []);
        setStaff(sRes.data?.data ?? []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load clients and staff');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, tenantPath]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientChange = (clientId) => {
    setForm((prev) => {
      const next = { ...prev, client_id: clientId };
      if (!clientId) {
        next.reviewee_staff_id = '';
        return next;
      }
      const c = clients.find((x) => String(x.id) === String(clientId));
      const aid = c?.assigned_staff_id;
      if (aid != null && aid !== '') {
        next.reviewee_staff_id = String(aid);
      } else {
        next.reviewee_staff_id = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.review_type || !form.reviewee_staff_id || !form.reviewer_staff_id) {
      setError('Client, review type, reviewee (SC), and reviewer are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fileReviewAPI.createReview({
        client_id: form.client_id,
        review_type: form.review_type,
        reviewee_staff_id: form.reviewee_staff_id,
        reviewer_staff_id: form.reviewer_staff_id,
        review_date: form.review_date || null,
        sc_due_date: form.sc_due_date || null,
      });
      const created = res.data?.data;
      if (created?.id) {
        navigate(tenantPath(`/lists/file-reviews/${created.id}`), { replace: true });
      } else {
        navigate(tenantPath('/lists/file-reviews'), { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create review');
    } finally {
      setSaving(false);
    }
  };

  const staffName = (s) => [s.last_name, s.first_name].filter(Boolean).join(', ') || `Staff #${s.id}`;
  const clientName = (c) => [c.last_name, c.first_name].filter(Boolean).join(', ') || `Client #${c.id}`;

  const headerBlock = (
    <div className="file-review-create-header">
      <div className="file-review-create-title-row">
        <h1 className="file-review-create-title">New File Review</h1>
        <InfoIconButton
          ariaExpanded={infoOpen}
          onClick={() => setInfoOpen((prev) => !prev)}
          ariaControls="file-review-create-page-info"
          ariaLabel="Show page description"
          ariaLabelExpanded="Hide page description"
          className="file-review-create-info-icon"
        />
      </div>
      {infoOpen && (
        <p id="file-review-create-page-info" className="file-review-create-info-text">
          {PAGE_INFO_TEXT}
        </p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="file-review-create-page">
        {headerBlock}
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="file-review-create-page">
      {headerBlock}

      {error && <p className="error-msg">{error}</p>}

      <form onSubmit={handleSubmit} className="file-review-create-form">
        <div className="form-group">
          <label htmlFor="fr-client_id">Client *</label>
          <select
            id="fr-client_id"
            value={form.client_id}
            onChange={(e) => handleClientChange(e.target.value)}
            required
          >
            <option value="">Select a client...</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{clientName(c)}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="fr-review_type">Review type *</label>
          <select
            id="fr-review_type"
            value={form.review_type}
            onChange={(e) => handleChange('review_type', e.target.value)}
            required
          >
            {REVIEW_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="fr-reviewee_staff_id">Reviewee (Service coordinator) *</label>
          <select
            id="fr-reviewee_staff_id"
            value={form.reviewee_staff_id}
            onChange={(e) => handleChange('reviewee_staff_id', e.target.value)}
            required
          >
            <option value="">Select SC...</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{staffName(s)}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="fr-reviewer_staff_id">Reviewer *</label>
          <select
            id="fr-reviewer_staff_id"
            value={form.reviewer_staff_id}
            onChange={(e) => handleChange('reviewer_staff_id', e.target.value)}
            required
          >
            <option value="">Select reviewer...</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{staffName(s)}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="fr-review_date">Review date</label>
          <input
            id="fr-review_date"
            type="date"
            value={form.review_date}
            onChange={(e) => handleChange('review_date', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="fr-sc_due_date">SC due date</label>
          <input
            id="fr-sc_due_date"
            type="date"
            value={form.sc_due_date}
            onChange={(e) => handleChange('sc_due_date', e.target.value)}
          />
        </div>
        <div className="form-actions">
          <Link to={tenantPath('/lists/file-reviews')} className="btn-cancel">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create review'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FileReviewCreate;
